import React, { useState, useContext } from "react";
import * as XLSX from "xlsx";
import {
  Upload,
  Download,
  AlertCircle,
  CheckCircle,
  XCircle,
  MapPin,
  Link as LinkIcon,
} from "lucide-react";
import { LanguageContext } from "./LanguageContext";
import { ThemeContext } from "./theme";
import { apiFetch } from "../context/AuthContext";

const LocationScheduleImport = ({ buttonStyle = "default" }) => {
  const { language } = useContext(LanguageContext);
  const { theme } = useContext(ThemeContext);
  const isRTL = language === "ar";

  const [uploading, setUploading] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [showResultModal, setShowResultModal] = useState(false);

  const BASE_URL = process.env.REACT_APP_BASE_URL || "https://nexploy-eg.com";
  // Centralized auth handled by apiFetch; no axios needed

  // ===============================================
  // TRANSLATIONS - الترجمات
  // ===============================================
  const translations = {
    uploading: {
      en: "Uploading...",
      ar: "جاري الرفع...",
    },
    locationSchedule: {
      en: "Import Location Schedule",
      ar: "إدخال جدول المواقع",
    },
    scheduleTemplate: {
      en: "Schedule Template",
      ar: "نموذج الجدول",
    },
    importSuccessful: {
      en: "Import Successful",
      ar: "تم الاستيراد بنجاح",
    },
    importFailed: {
      en: "Import Failed",
      ar: "فشل الاستيراد",
    },
    totalRecords: {
      en: "Total Records",
      ar: "إجمالي السجلات",
    },
    created: {
      en: "Created",
      ar: "تم الإنشاء",
    },
    updated: {
      en: "Updated",
      ar: "تم التحديث",
    },
    failed: {
      en: "Failed",
      ar: "فشل",
    },
    importErrors: {
      en: "Import Errors",
      ar: "أخطاء الاستيراد",
    },
    close: {
      en: "Close",
      ar: "إغلاق",
    },
    errorOccurred: {
      en: "An unknown error occurred during import.",
      ar: "حدث خطأ غير معروف أثناء الاستيراد.",
    },
    acceptedFormats: {
      en: "Accepted Formats",
      ar: "الصيغ المقبولة",
    },
    acceptedFormatsText: {
      en: "CSV, XLSX, XLS files are accepted",
      ar: "يتم قبول ملفات CSV و XLSX و XLS",
    },
    googleMapsLinks: {
      en: "Google Maps Links",
      ar: "روابط خرائط جوجل",
    },
    googleMapsInfo: {
      en: "Use any Google Maps link format for location fields:",
      ar: "استخدم أي صيغة من روابط خرائط جوجل لحقول المواقع:",
    },
    exampleLinks: {
      en: "Example Links",
      ar: "أمثلة الروابط",
    },
    // Column Headers
    infinityCode: {
      en: "Infinity Code",
      ar: "كود الموظف",
    },
    employeeName: {
      en: "Employee Name",
      ar: "اسم الموظف",
    },
    clientName: {
      en: "Client Name",
      ar: "اسم العميل",
    },
    workingCompany: {
      en: "Working Company",
      ar: "شركة العمل",
    },
    sundayLocation: {
      en: "Sunday Location",
      ar: "موقع الأحد",
    },
    sundayLink: {
      en: "Sunday Link",
      ar: "رابط الأحد",
    },
    sundayStartTime: {
      en: "Sunday Start Time",
      ar: "وقت بداية الأحد",
    },
    sundayEndTime: {
      en: "Sunday End Time",
      ar: "وقت نهاية الأحد",
    },
    mondayLocation: {
      en: "Monday Location",
      ar: "موقع الاثنين",
    },
    mondayLink: {
      en: "Monday Link",
      ar: "رابط الاثنين",
    },
    mondayStartTime: {
      en: "Monday Start Time",
      ar: "وقت بداية الاثنين",
    },
    mondayEndTime: {
      en: "Monday End Time",
      ar: "وقت نهاية الاثنين",
    },
    tuesdayLocation: {
      en: "Tuesday Location",
      ar: "موقع الثلاثاء",
    },
    tuesdayLink: {
      en: "Tuesday Link",
      ar: "رابط الثلاثاء",
    },
    tuesdayStartTime: {
      en: "Tuesday Start Time",
      ar: "وقت بداية الثلاثاء",
    },
    tuesdayEndTime: {
      en: "Tuesday End Time",
      ar: "وقت نهاية الثلاثاء",
    },
    wednesdayLocation: {
      en: "Wednesday Location",
      ar: "موقع الأربعاء",
    },
    wednesdayLink: {
      en: "Wednesday Link",
      ar: "رابط الأربعاء",
    },
    wednesdayStartTime: {
      en: "Wednesday Start Time",
      ar: "وقت بداية الأربعاء",
    },
    wednesdayEndTime: {
      en: "Wednesday End Time",
      ar: "وقت نهاية الأربعاء",
    },
    thursdayLocation: {
      en: "Thursday Location",
      ar: "موقع الخميس",
    },
    thursdayLink: {
      en: "Thursday Link",
      ar: "رابط الخميس",
    },
    thursdayStartTime: {
      en: "Thursday Start Time",
      ar: "وقت بداية الخميس",
    },
    thursdayEndTime: {
      en: "Thursday End Time",
      ar: "وقت نهاية الخميس",
    },
    fridayLocation: {
      en: "Friday Location",
      ar: "موقع الجمعة",
    },
    fridayLink: {
      en: "Friday Link",
      ar: "رابط الجمعة",
    },
    fridayStartTime: {
      en: "Friday Start Time",
      ar: "وقت بداية الجمعة",
    },
    fridayEndTime: {
      en: "Friday End Time",
      ar: "وقت نهاية الجمعة",
    },
    saturdayLocation: {
      en: "Saturday Location",
      ar: "موقع السبت",
    },
    saturdayLink: {
      en: "Saturday Link",
      ar: "رابط السبت",
    },
    saturdayStartTime: {
      en: "Saturday Start Time",
      ar: "وقت بداية السبت",
    },
    saturdayEndTime: {
      en: "Saturday End Time",
      ar: "وقت نهاية السبت",
    },
  };

  const t = (key) =>
    translations[key]?.[language] || translations[key]?.en || key;

  // ===============================================
  // HANDLERS - معالجات الأحداث
  // ===============================================
  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    setUploading(true);
    setImportResult(null);

    try {
      const viewType = localStorage.getItem("managerViewType") || "manager";

      const response = await apiFetch(
        `${BASE_URL}/employees/import-location-schedule?viewType=${encodeURIComponent(viewType)}`,
        {
          method: "POST",
          body: formData,
          headers: {
            "x-view-type": viewType,
          },
        },
      );

      const data = await response.json();

      if (response.ok) {
        setImportResult({
          success: true,
          data: data,
        });
      } else {
        setImportResult({
          success: false,
          error: data.details || data.error || "Upload failed",
        });
      }
      setShowResultModal(true);
    } catch (error) {
      setImportResult({
        success: false,
        error: error.message || "Network error occurred",
      });
      setShowResultModal(true);
    } finally {
      setUploading(false);
      event.target.value = null;
    }
  };

  const downloadTemplate = () => {
    const headers = [
      t("infinityCode"),
      t("employeeName"),
      t("clientName"),
      t("workingCompany"),
      t("sundayLocation"),
      t("sundayLink"),
      t("sundayStartTime"),
      t("sundayEndTime"),
      t("mondayLocation"),
      t("mondayLink"),
      t("mondayStartTime"),
      t("mondayEndTime"),
      t("tuesdayLocation"),
      t("tuesdayLink"),
      t("tuesdayStartTime"),
      t("tuesdayEndTime"),
      t("wednesdayLocation"),
      t("wednesdayLink"),
      t("wednesdayStartTime"),
      t("wednesdayEndTime"),
      t("thursdayLocation"),
      t("thursdayLink"),
      t("thursdayStartTime"),
      t("thursdayEndTime"),
      t("fridayLocation"),
      t("fridayLink"),
      t("fridayStartTime"),
      t("fridayEndTime"),
      t("saturdayLocation"),
      t("saturdayLink"),
      t("saturdayStartTime"),
      t("saturdayEndTime"),
    ];

    const sampleData = [
      "1001",
      language === "ar" ? "أحمد محمد" : "John Doe",
      "Infinity",
      language === "ar" ? "المكتب الرئيسي" : "Main Office",
      // Sunday
      language === "ar" ? "المكتب الرئيسي" : "Main Office",
      "https://maps.google.com/?q=30.0444,31.2357",
      "09:00",
      "17:00",
      // Monday
      language === "ar" ? "المكتب الرئيسي" : "Main Office",
      "https://www.google.com/maps/@30.0444,31.2357,15z",
      "09:00",
      "17:00",
      // Tuesday
      language === "ar" ? "الفرع" : "Branch Office",
      "https://maps.google.com/maps?q=30.0500,31.2400",
      "09:00",
      "17:00",
      // Wednesday
      language === "ar" ? "الفرع" : "Branch Office",
      "https://www.google.com/maps/place/30.0500,31.2400",
      "09:00",
      "17:00",
      // Thursday
      language === "ar" ? "المكتب الرئيسي" : "Main Office",
      "https://maps.google.com/?q=30.0444,31.2357",
      "09:00",
      "17:00",
      // Friday (Day off)
      "",
      "",
      "",
      "",
      // Saturday (Day off)
      "",
      "",
      "",
      "",
    ];

    const wsData = [headers, sampleData];
    const ws = XLSX.utils.aoa_to_sheet(wsData);

    // Auto-size columns based on content
    const colWidths = headers.map((h, i) => {
      const sampleVal = sampleData[i] || "";
      return { wch: Math.max(h.length, String(sampleVal).length, 12) + 2 };
    });
    ws["!cols"] = colWidths;

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Template");

    XLSX.writeFile(wb, `location_schedule_template_${language}.xlsx`);
  };

  // ===============================================
  // STYLES - الأنماط
  // ===============================================
  const getButtonStyle = () => {
    if (buttonStyle === "attendance") {
      return {
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "8px 16px",
        borderRadius: "8px",
        border: "1px solid rgba(255,255,255,0.15)",
        background:
          theme === "dark" ? "rgba(30, 41, 59, 0.8)" : "rgba(51, 65, 85, 0.12)",
        color: theme === "dark" ? "#f1f5f9" : "#1e293b",
        fontWeight: "500",
        cursor: uploading ? "not-allowed" : "pointer",
        opacity: uploading ? 0.6 : 1,
        transition: "all 0.2s ease",
        flexDirection: isRTL ? "row-reverse" : "row",
      };
    }

    return {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      padding: "8px 16px",
      borderRadius: "8px",
      border: "1px solid rgba(255,255,255,0.15)",
      background:
        theme === "dark" ? "rgba(30, 41, 59, 0.8)" : "rgba(51, 65, 85, 0.12)",
      color: theme === "dark" ? "#f1f5f9" : "#1e293b",
      fontWeight: "500",
      cursor: "pointer",
      flexDirection: isRTL ? "row-reverse" : "row",
      transition: "all 0.2s ease",
    };
  };

  const styles = {
    buttonContainer: {
      display: "flex",
      gap: "12px",
      flexWrap: "wrap",
      direction: isRTL ? "rtl" : "ltr",
    },

    uploadButton: getButtonStyle(),

    modal: {
      position: "fixed",
      top: 0,
      left: 0,
      width: "100%",
      height: "100%",
      backgroundColor: "rgba(0, 0, 0, 0.6)",
      backdropFilter: "blur(8px)",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      zIndex: 2000,
      padding: "20px",
    },

    modalContent: {
      background:
        theme === "dark"
          ? "rgba(30, 41, 59, 0.98)"
          : "rgba(255, 255, 255, 0.98)",
      borderRadius: "clamp(16px, 2.5vw, 20px)",
      padding: "clamp(24px, 4vw, 32px)",
      width: "min(90vw, 600px)",
      maxHeight: "80vh",
      overflowY: "auto",
      boxShadow: "0 20px 60px rgba(0, 0, 0, 0.4)",
      direction: isRTL ? "rtl" : "ltr",
      // ← ضيف الأسطر دي عشان تخفي الـ scrollbar
      scrollbarWidth: "none", // للـ Firefox
      msOverflowStyle: "none", // للـ IE و Edge
      WebkitOverflowScrolling: "touch", // للـ iOS
      scrollbarWidth: "none", // للـ Firefox
      msOverflowStyle: "none", // للـ IE و Edge
    },

    modalHeader: {
      display: "flex",
      alignItems: "center",
      gap: "16px",
      marginBottom: "clamp(20px, 3vw, 24px)",
      paddingBottom: "clamp(14px, 2.5vw, 16px)",
      borderBottom:
        theme === "dark"
          ? "1px solid rgba(255, 255, 255, 0.1)"
          : "1px solid rgba(0, 0, 0, 0.1)",
      flexDirection: isRTL ? "row-reverse" : "row",
    },

    modalTitle: {
      fontSize: "clamp(18px, 3vw, 24px)",
      fontWeight: "700",
      color: theme === "dark" ? "#fff" : "#1a1a1a",
      margin: 0,
      textAlign: isRTL ? "right" : "left",
    },

    statsGrid: {
      display: "grid",
      gridTemplateColumns:
        "repeat(auto-fit, minmax(clamp(120px, 20vw, 150px), 1fr))",
      gap: "clamp(12px, 2vw, 16px)",
      marginBottom: "clamp(20px, 3vw, 24px)",
    },

    statCard: {
      background:
        theme === "dark" ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.02)",
      borderRadius: "12px",
      padding: "clamp(14px, 2.5vw, 18px)",
      border:
        theme === "dark"
          ? "1px solid rgba(255, 255, 255, 0.1)"
          : "1px solid rgba(0, 0, 0, 0.05)",
      textAlign: "center",
    },

    statLabel: {
      fontSize: "clamp(11px, 1.5vw, 12px)",
      fontWeight: "600",
      color: theme === "dark" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.6)",
      marginBottom: "8px",
      textTransform: "uppercase",
      letterSpacing: "0.5px",
    },

    statValue: {
      fontSize: "clamp(24px, 4vw, 32px)",
      fontWeight: "800",
      margin: 0,
    },

    errorBox: {
      background:
        theme === "dark"
          ? "rgba(239, 68, 68, 0.15)"
          : "rgba(239, 68, 68, 0.08)",
      border: "1px solid rgba(239, 68, 68, 0.4)",
      borderRadius: "12px",
      padding: "16px",
      marginTop: "16px",
      maxHeight: "200px",
      overflowY: "auto",
    },

    errorTitle: {
      fontWeight: "700",
      fontSize: "14px",
      marginBottom: "12px",
      color: "#ef4444",
      display: "flex",
      alignItems: "center",
      gap: "8px",
      flexDirection: isRTL ? "row-reverse" : "row",
    },

    errorText: {
      color: theme === "dark" ? "#fca5a5" : "#dc2626",
      fontSize: "clamp(11px, 1.5vw, 13px)",
      lineHeight: "1.6",
      marginBottom: "8px",
      textAlign: isRTL ? "right" : "left",
    },

    closeButton: {
      background:
        theme === "dark" ? "rgba(30, 41, 59, 0.8)" : "rgba(51, 65, 85, 0.12)",
      color: theme === "dark" ? "#f1f5f9" : "#1e293b",
      border: "1px solid rgba(255,255,255,0.15)",
      borderRadius: "8px",
      padding: "8px 16px",
      fontSize: "13px",
      fontWeight: "500",
      cursor: "pointer",
      marginTop: "16px",
      width: "100%",
      transition: "all 0.2s ease",
    },

    infoBox: {
      background:
        theme === "dark"
          ? "rgba(59, 130, 246, 0.1)"
          : "rgba(59, 130, 246, 0.05)",
      border:
        theme === "dark"
          ? "1px solid rgba(59, 130, 246, 0.3)"
          : "1px solid rgba(59, 130, 246, 0.2)",
      borderRadius: "10px",
      padding: "clamp(12px, 2vw, 16px)",
      marginTop: "clamp(12px, 2vw, 16px)",
      fontSize: "clamp(11px, 1.5vw, 13px)",
      color: theme === "dark" ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.7)",
      lineHeight: "1.6",
    },

    infoTitle: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      fontWeight: "600",
      marginBottom: "8px",
      color: "#3b82f6",
      flexDirection: isRTL ? "row-reverse" : "row",
    },

    linkExample: {
      background:
        theme === "dark"
          ? "rgba(16, 185, 129, 0.1)"
          : "rgba(16, 185, 129, 0.05)",
      border:
        theme === "dark"
          ? "1px solid rgba(16, 185, 129, 0.3)"
          : "1px solid rgba(16, 185, 129, 0.2)",
      borderRadius: "8px",
      padding: "clamp(10px, 1.5vw, 12px)",
      marginTop: "8px",
      fontSize: "clamp(10px, 1.3vw, 12px)",
      fontFamily: "monospace",
      color: "#10b981",
      wordBreak: "break-all",
      textAlign: isRTL ? "right" : "left",
    },
  };

  // ===============================================
  // RENDER
  // ===============================================
  return (
    <>
      <div style={styles.buttonContainer}>
        <label
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "8px 16px",
            borderRadius: "8px",
            border: "1px solid rgba(255,255,255,0.15)",
            background:
              theme === "dark"
                ? "rgba(30, 41, 59, 0.8)"
                : "rgba(51, 65, 85, 0.12)",
            color: theme === "dark" ? "#f1f5f9" : "#1e293b",
            fontWeight: "500",
            fontSize: "13px", // ← matches other action buttons
            fontFamily: "inherit", // ← inherit page font
            cursor: uploading ? "not-allowed" : "pointer",
            opacity: uploading ? 0.6 : 1,
            transition: "all 0.2s ease",
            flexDirection: isRTL ? "row-reverse" : "row",
            userSelect: "none", // ← prevent text selection on click
            whiteSpace: "nowrap", // ← prevent text wrapping
            lineHeight: "1.5", // ← match button line height
          }}
          onMouseEnter={(e) => {
            if (!uploading) {
              e.currentTarget.style.background =
                theme === "dark"
                  ? "rgba(30, 41, 59, 1)"
                  : "rgba(51, 65, 85, 0.2)";
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background =
              theme === "dark"
                ? "rgba(30, 41, 59, 0.8)"
                : "rgba(51, 65, 85, 0.12)";
          }}
        >
          <MapPin size={16} />
          {uploading ? t("uploading") : t("locationSchedule")}
          <input
            type="file"
            accept=".xlsx,.xls,.csv"
            onChange={handleFileUpload}
            disabled={uploading}
            style={{ display: "none" }}
          />
        </label>
        <button
          style={styles.uploadButton}
          onClick={downloadTemplate}
          onMouseEnter={(e) => {
            e.currentTarget.style.background =
              theme === "dark"
                ? "rgba(30, 41, 59, 1)"
                : "rgba(51, 65, 85, 0.2)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background =
              theme === "dark"
                ? "rgba(30, 41, 59, 0.8)"
                : "rgba(51, 65, 85, 0.12)";
          }}
        >
          <Download size={16} />
          {t("scheduleTemplate")}
        </button>
      </div>

      {showResultModal && (
        <div style={styles.modal} onClick={() => setShowResultModal(false)}>
          <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              {importResult?.success ? (
                <CheckCircle size={28} color="#22c55e" />
              ) : (
                <XCircle size={28} color="#ef4444" />
              )}
              <h2 style={styles.modalTitle}>
                {importResult?.success
                  ? t("importSuccessful")
                  : t("importFailed")}
              </h2>
            </div>

            {importResult?.success && (
              <div>
                <div style={styles.statsGrid}>
                  <div style={styles.statCard}>
                    <div style={styles.statLabel}>{t("totalRecords")}</div>
                    <p style={styles.statValue}>
                      {importResult.data.data.totalEmployees}
                    </p>
                  </div>
                  <div style={styles.statCard}>
                    <div style={styles.statLabel}>{t("created")}</div>
                    <p style={styles.statValue}>
                      {importResult.data.data.createdCount}
                    </p>
                  </div>
                  <div style={styles.statCard}>
                    <div style={styles.statLabel}>{t("updated")}</div>
                    <p style={styles.statValue}>
                      {importResult.data.data.updatedCount}
                    </p>
                  </div>
                  <div style={styles.statCard}>
                    <div style={styles.statLabel}>{t("failed")}</div>
                    <p style={styles.statValue}>
                      {importResult.data.data.failedCount}
                    </p>
                  </div>
                </div>

                {importResult.data.errors &&
                  importResult.data.errors.length > 0 && (
                    <div style={styles.errorBox}>
                      <div style={styles.errorTitle}>
                        <AlertCircle size={16} />
                        {t("importErrors")}
                      </div>
                      {importResult.data.errors.map((err, index) => (
                        <div key={index} style={styles.errorText}>
                          • {err}
                        </div>
                      ))}
                    </div>
                  )}

                <div style={styles.infoBox}>
                  <div style={styles.infoTitle}>
                    <AlertCircle size={16} />
                    {t("acceptedFormats")}
                  </div>
                  <p style={{ margin: "0 0 8px 0" }}>
                    {t("acceptedFormatsText")}
                  </p>
                </div>

                <div style={styles.infoBox}>
                  <div style={styles.infoTitle}>
                    <LinkIcon size={16} />
                    {t("googleMapsLinks")}
                  </div>
                  <p style={{ margin: "0 0 8px 0" }}>{t("googleMapsInfo")}</p>
                  <div style={{ marginTop: "12px" }}>
                    <strong style={{ display: "block", marginBottom: "8px" }}>
                      {t("exampleLinks")}:
                    </strong>
                    <div style={styles.linkExample}>
                      https://maps.google.com/?q=30.0444,31.2357
                    </div>
                    <div style={styles.linkExample}>
                      https://www.google.com/maps/@30.0444,31.2357,15z
                    </div>
                    <div style={styles.linkExample}>
                      https://maps.google.com/maps?q=30.0500,31.2400
                    </div>
                    <div style={styles.linkExample}>
                      https://www.google.com/maps/place/30.0500,31.2400
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!importResult?.success && (
              <div>
                <p
                  style={{
                    color: theme === "dark" ? "#fca5a5" : "#dc2626",
                    textAlign: isRTL ? "right" : "left",
                  }}
                >
                  {importResult.error || t("errorOccurred")}
                </p>
              </div>
            )}

            <button
              style={styles.closeButton}
              onClick={() => setShowResultModal(false)}
              onMouseEnter={(e) => {
                e.currentTarget.style.background =
                  theme === "dark"
                    ? "rgba(30, 41, 59, 1)"
                    : "rgba(51, 65, 85, 0.2)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background =
                  theme === "dark"
                    ? "rgba(30, 41, 59, 0.8)"
                    : "rgba(51, 65, 85, 0.12)";
              }}
            >
              {t("close")}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default LocationScheduleImport;